'use client'

import { isEmpty, isNil } from 'lodash-es'
import { useTranslations } from 'next-intl'
import { RefObject, useEffect, useState } from 'react'

import { ZoomIn } from '@/entities/map'
import { NoSearchResult } from '@/entities/room'
import { cn } from '@/shared/lib'
import { Separator, Spinner } from '@/shared/ui'

import { useMapToast } from '../../lib/toast'
import { useRooms } from '../../model/rooms'
import { MobileRoomItem } from '../room-item'

const ZoomInMessage = () => {
  const t = useTranslations('map')

  return (
    <div
      className={cn(
        'mt-7 flex w-full shrink-0 flex-col items-center justify-center gap-6 landscape:mt-0',
        'transition-all duration-300 group-data-[half-collapsed=false]:mt-25 group-data-[half-collapsed=false]:landscape:mt-0',
      )}
    >
      <ZoomIn />
      <h3 className="typo-headline3 text-center font-bold text-black">
        {t('list.zoom_message')}
      </h3>
    </div>
  )
}

const NoResultMessage = () => {
  const t = useTranslations('map')

  return (
    <div
      className={cn(
        'flex w-full shrink-0 flex-col items-center justify-center',
        'transition-all duration-300 group-data-[half-collapsed=false]:mt-25 group-data-[half-collapsed=false]:landscape:mt-0',
      )}
    >
      <NoSearchResult className="mb-6" />
      <h3 className="typo-headline3 mb-4 text-center font-bold text-black">
        {t('list.no_result.title')}
      </h3>
      <p className="typo-label1 text-center whitespace-break-spaces text-gray-900">
        {t('list.no_result.description')}
      </p>
    </div>
  )
}

type InnerRoomListProps = {
  isMounted: boolean
  scrollToTop: () => void
}

const InnerRoomList = ({ isMounted, scrollToTop }: InnerRoomListProps) => {
  const { rooms, ref, loading, isFirstPage, isFetching } = useRooms({
    isMounted,
  })

  useMapToast({ isMounted, rooms, isFetching })

  useEffect(() => {
    if (isFirstPage) {
      scrollToTop()
    }
  }, [rooms])

  if (!isMounted || loading)
    return <Spinner className="mt-[140px] items-start" />
  if (isNil(rooms)) return <ZoomInMessage />
  if (isEmpty(rooms)) return <NoResultMessage />

  return rooms.map((room, index) => {
    const isLastItem = index === rooms.length - 1

    return (
      <li key={room.rid} className="h-fit" ref={isLastItem ? ref : null}>
        <MobileRoomItem room={room} />
        {!isLastItem && <Separator className="my-5" />}
      </li>
    )
  })
}

type MobileRoomListProps = {
  ref: RefObject<HTMLUListElement | null>
  onScroll: () => void
}

export const MobileRoomList = ({
  ref: refProp,
  onScroll,
}: MobileRoomListProps) => {
  const [isMounted, setIsMounted] = useState(false)

  const scrollToTop = () => {
    refProp.current?.scroll({ top: 0, behavior: 'smooth' })
  }

  useEffect(() => {
    setIsMounted(true)
  }, [])

  return (
    <ul
      ref={refProp}
      onScroll={onScroll}
      className={cn('mb-5 flex-1 overflow-y-auto overscroll-contain px-4 pt-1')}
    >
      <InnerRoomList isMounted={isMounted} scrollToTop={scrollToTop} />
    </ul>
  )
}
